import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { TextField, Switch, CircularProgress } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ModalShell from '../common/ModalShell';
import { sa, PillButton } from '../superAdminUi';
import {
  grantUserCredits,
  increaseSchoolCredits,
  increaseGroupCredits,
  setUserMonthAllowance,
  setUserAllowance,
} from '../../services/api/eiCreditsService';
import { monthLabel, formatCredits } from '../../utils/creditFormat';

// The AI credit action modals, rebuilt on ModalShell to the approved Figma: a titled form card
// with a coloured accent, then a confirm -> submitting -> success/error flow rendered as the
// design's centred status panels. Presentation only — the service payloads are unchanged.

const toCredits = (value) => {
  const n = parseInt(value, 10);
  return Number.isFinite(n) ? n : NaN;
};

// --- Shared styled fragments ------------------------------------------------

const BodyText = styled.p`
  font-family: ${sa.font.family};
  font-size: 14px;
  color: ${sa.color.textGray};
  line-height: 1.5;
  margin: 0 0 16px;
`;
const FieldLabel = styled.label`
  display: block;
  font-family: ${sa.font.family};
  font-weight: ${sa.font.bold};
  font-size: 13px;
  color: ${sa.color.text};
  margin: ${(p) => (p.$spaced ? '16px 0 6px' : '0 0 6px')};
`;
const Helper = styled.p`
  font-family: ${sa.font.family};
  font-size: 12px;
  color: ${sa.color.textGray};
  margin: 8px 0 0;
`;
const InfoBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  background: #eaf6fb;
  color: ${sa.color.blue};
  border-radius: ${sa.radius.sm};
  padding: 10px 12px;
  margin-top: 14px;
  font-family: ${sa.font.family};
  font-size: 12.5px;
  line-height: 1.4;

  svg {
    font-size: 16px;
    flex-shrink: 0;
    margin-top: 1px;
  }
`;
const WarnBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fdf3e0;
  color: #9a6b00;
  border-radius: ${sa.radius.sm};
  padding: 8px 12px;
  margin-bottom: 16px;
  font-family: ${sa.font.family};
  font-weight: ${sa.font.bold};
  font-size: 12.5px;

  svg {
    font-size: 16px;
    flex-shrink: 0;
  }
`;
const CurrentRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;
const CurrentLabel = styled.span`
  font-family: ${sa.font.family};
  font-size: 13px;
  color: ${sa.color.textGray};
`;
const CurrentValue = styled.span`
  font-family: ${sa.font.family};
  font-weight: ${sa.font.bold};
  font-size: 14px;
  color: ${sa.color.text};
`;
const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
`;
const ToggleLabel = styled.span`
  font-family: ${sa.font.family};
  font-size: 13px;
  color: ${sa.color.text};
`;
const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 24px;
`;
const StatusWrap = styled.div`
  text-align: center;
`;
const IconCircle = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: ${(p) => p.$bg};

  svg {
    color: ${sa.color.white};
    font-size: 30px;
  }
`;
const StatusBody = styled.p`
  font-family: ${sa.font.family};
  font-size: 14px;
  color: ${sa.color.textGray};
  line-height: 1.5;
  margin: 16px 0 0;
`;

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: sa.radius.sm,
    fontFamily: sa.font.family,
    fontSize: '14px',
  },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: sa.color.stroke },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: sa.color.textGray },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: sa.color.teal },
};
const switchSx = {
  '& .MuiSwitch-switchBase.Mui-checked': { color: sa.color.teal },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: sa.color.teal, opacity: 1 },
};

// A common confirm -> submitting -> success/error shell so every credit action modal behaves
// identically. `submit` returns the service promise; each concrete modal supplies its titles,
// accent, form fields and success copy — the shell owns the state machine and the footer.
function CreditActionModal({
  open,
  onClose,
  onSuccess,
  title,
  accent,
  confirmLabel,
  confirmVariant,
  canSubmit,
  submit,
  successTitle,
  successText,
  errorTitle,
  children,
}) {
  const [status, setStatus] = useState('form'); // 'form' | 'submitting' | 'success' | 'error'
  const [errorText, setErrorText] = useState(null);

  const close = () => {
    setStatus('form');
    setErrorText(null);
    onClose();
  };

  const handleConfirm = async () => {
    setStatus('submitting');
    setErrorText(null);
    try {
      await submit();
      setStatus('success');
      if (onSuccess) onSuccess();
    } catch (err) {
      setErrorText(err?.message || 'An error occurred. Please try again.');
      setStatus('error');
    }
  };

  const busy = status === 'submitting';

  const shellTitle =
    status === 'success'
      ? successTitle
      : status === 'error'
      ? <span style={{ color: sa.color.pink }}>{errorTitle}</span>
      : title;
  const shellAccent =
    status === 'success' ? sa.color.teal : status === 'error' ? sa.color.pink : accent;

  return (
    <ModalShell open={open} onClose={close} title={shellTitle} accent={shellAccent} width={{ xs: '95%', sm: 440 }}>
      {status === 'success' ? (
        <StatusWrap>
          <IconCircle $bg={sa.color.teal}>
            <CheckIcon />
          </IconCircle>
          <StatusBody>{successText}</StatusBody>
          <PillButton type='button' $size='lg' onClick={close} style={{ width: '100%', marginTop: 20 }}>
            Close
          </PillButton>
        </StatusWrap>
      ) : status === 'error' ? (
        <StatusWrap>
          <IconCircle $bg={sa.color.pink}>
            <CloseIcon />
          </IconCircle>
          <StatusBody>{errorText}</StatusBody>
          <Footer>
            <PillButton type='button' $size='lg' $variant='outline' onClick={close}>
              Cancel
            </PillButton>
            <PillButton type='button' $size='lg' $variant='danger' onClick={handleConfirm}>
              Try Again
            </PillButton>
          </Footer>
        </StatusWrap>
      ) : (
        <>
          {children}
          <Footer>
            <PillButton type='button' $size='lg' $variant='outline' onClick={close} disabled={busy}>
              Cancel
            </PillButton>
            <PillButton
              type='button'
              $size='lg'
              $variant={confirmVariant === 'teal' ? undefined : confirmVariant}
              onClick={handleConfirm}
              disabled={busy || !canSubmit}
            >
              {busy ? <CircularProgress size={16} sx={{ color: sa.color.white }} /> : confirmLabel}
            </PillButton>
          </Footer>
        </>
      )}
    </ModalShell>
  );
}

CreditActionModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  title: PropTypes.node.isRequired,
  accent: PropTypes.string,
  confirmLabel: PropTypes.string.isRequired,
  confirmVariant: PropTypes.oneOf(['teal', 'danger', 'purple']),
  canSubmit: PropTypes.bool.isRequired,
  submit: PropTypes.func.isRequired,
  successTitle: PropTypes.string.isRequired,
  successText: PropTypes.string.isRequired,
  errorTitle: PropTypes.string.isRequired,
  children: PropTypes.node,
};
CreditActionModal.defaultProps = { confirmVariant: 'teal' };

// Grant additional one-off credits to a single user for the selected month.
export function GrantCreditsModal({ open, onClose, onSuccess, period, schoolId, user }) {
  const [credits, setCredits] = useState('');
  const [reason, setReason] = useState('');
  const amount = toCredits(credits);
  const valid = Number.isFinite(amount) && amount > 0;
  const name = user?.name || 'this user';

  return (
    <CreditActionModal
      open={open}
      onClose={onClose}
      onSuccess={onSuccess}
      title='Grant Credits'
      accent={sa.color.purple}
      confirmLabel='Grant Credits'
      canSubmit={valid}
      submit={() =>
        grantUserCredits({ period, schoolId, userId: user?.userId, credits: amount, reason: reason || undefined })
      }
      successTitle='Credits Granted!'
      successText={`${amount} credits added to ${name} for ${monthLabel(period)}.`}
      errorTitle='Failed to Grant Credits'
    >
      <BodyText>
        Grant additional credits to {name} for this month? This does not change their standing
        monthly allowance.
      </BodyText>
      <FieldLabel htmlFor='grant-credits'>Number of credits</FieldLabel>
      <TextField
        id='grant-credits'
        fullWidth
        size='small'
        type='number'
        value={credits}
        onChange={(e) => setCredits(e.target.value)}
        inputProps={{ min: 1 }}
        sx={inputSx}
      />
      <Helper>These credits reset next month.</Helper>
      <FieldLabel htmlFor='grant-reason' $spaced>
        Reason (optional)
      </FieldLabel>
      <TextField
        id='grant-reason'
        fullWidth
        size='small'
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        multiline
        minRows={2}
        sx={inputSx}
      />
    </CreditActionModal>
  );
}

GrantCreditsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  period: PropTypes.string.isRequired,
  schoolId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  user: PropTypes.object,
};

// Increase a school's or group's total for the month; the extra is split equally across its
// users (school) or schools (group). `scope` selects the target and the service call.
export function IncreaseTotalModal({ open, onClose, onSuccess, scope, period, schoolId, groupId, entityName }) {
  const [credits, setCredits] = useState('');
  const [reason, setReason] = useState('');
  const amount = toCredits(credits);
  const valid = Number.isFinite(amount) && amount > 0;
  const isGroup = scope === 'group';
  const target = entityName || (isGroup ? 'Group' : 'School');

  return (
    <CreditActionModal
      open={open}
      onClose={onClose}
      onSuccess={onSuccess}
      title={`Increase Total Credits for ${target}`}
      accent={sa.color.purple}
      confirmLabel='Confirm Increase'
      canSubmit={valid}
      submit={() =>
        isGroup
          ? increaseGroupCredits({ period, groupId, credits: amount, reason: reason || undefined })
          : increaseSchoolCredits({ period, schoolId, credits: amount, reason: reason || undefined })
      }
      successTitle='Credits Increased!'
      successText={`Added ${amount} credits to ${target} for ${monthLabel(period)}.`}
      errorTitle='Failed to Increase Total'
    >
      <FieldLabel htmlFor='increase-credits'>Total credits increase</FieldLabel>
      <TextField
        id='increase-credits'
        fullWidth
        size='small'
        type='number'
        value={credits}
        onChange={(e) => setCredits(e.target.value)}
        inputProps={{ min: 1 }}
        sx={inputSx}
      />
      <InfoBox>
        <InfoOutlinedIcon />
        <span>
          The additional credits are shared equally across{' '}
          {isGroup ? "the group's schools" : "this school's users"}.
        </span>
      </InfoBox>
      <FieldLabel htmlFor='increase-reason' $spaced>
        Reason (optional)
      </FieldLabel>
      <TextField
        id='increase-reason'
        fullWidth
        size='small'
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        multiline
        minRows={2}
        sx={inputSx}
      />
    </CreditActionModal>
  );
}

IncreaseTotalModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  scope: PropTypes.oneOf(['school', 'group']).isRequired,
  period: PropTypes.string.isRequired,
  schoolId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  groupId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  entityName: PropTypes.string,
};

// Change a user's monthly credit allowance. "Apply permanently" OFF sets this month only
// (resets next month); ON sets the standing allowance for every future month.
export function ChangeCreditsModal({ open, onClose, onSuccess, period, schoolId, user }) {
  const [credits, setCredits] = useState('');
  const [permanent, setPermanent] = useState(false);
  const amount = toCredits(credits);
  const valid = Number.isFinite(amount) && amount >= 0;
  const name = user?.name || 'this user';
  const currentAllowance = Number(user?.allowance);
  const hasCurrent = Number.isFinite(currentAllowance);

  return (
    <CreditActionModal
      open={open}
      onClose={onClose}
      onSuccess={onSuccess}
      title={`Change Credits for ${user?.name || 'User'}`}
      accent={permanent ? sa.color.accentAmber : sa.color.purple}
      confirmLabel={permanent ? 'Save Permanently' : 'Save Changes'}
      canSubmit={valid}
      submit={() =>
        permanent
          ? setUserAllowance({ schoolId, userId: user?.userId, credits: amount })
          : setUserMonthAllowance({ period, schoolId, userId: user?.userId, credits: amount })
      }
      successTitle='Credits Updated!'
      successText={
        permanent
          ? `Set ${name}'s standing monthly allowance to ${amount} credits.`
          : `Set ${name}'s allowance to ${amount} credits for ${monthLabel(period)}.`
      }
      errorTitle='Failed to Update Credits'
    >
      {permanent && (
        <WarnBanner>
          <WarningAmberIcon />
          This is a permanent change.
        </WarnBanner>
      )}
      {hasCurrent && (
        <CurrentRow>
          <CurrentLabel>Current Allocation</CurrentLabel>
          <CurrentValue>{formatCredits(currentAllowance, { compact: false })} credits</CurrentValue>
        </CurrentRow>
      )}
      <FieldLabel htmlFor='change-credits'>New monthly credits</FieldLabel>
      <TextField
        id='change-credits'
        fullWidth
        size='small'
        type='number'
        value={credits}
        onChange={(e) => setCredits(e.target.value)}
        inputProps={{ min: 0 }}
        sx={inputSx}
      />
      <ToggleRow>
        <Switch
          checked={permanent}
          onChange={(e) => setPermanent(e.target.checked)}
          inputProps={{ 'aria-label': 'Apply permanently' }}
          sx={switchSx}
        />
        <ToggleLabel>Apply permanently</ToggleLabel>
      </ToggleRow>
      <Helper>
        {permanent
          ? 'Applies to every future month until changed again.'
          : `* Applies to ${monthLabel(period)} only.`}
      </Helper>
    </CreditActionModal>
  );
}

ChangeCreditsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  period: PropTypes.string.isRequired,
  schoolId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  user: PropTypes.object,
};
